import type { PlannedNudge } from '@/api/types';

// Shared labels for the onboarding nudge engine (LIM-3551): the per-user
// Nudges tab and the cross-user Nudges page read sequences the same way.

/** A sequence key as words: `first_action` → `first action`. */
export function sequenceLabel(sequence?: string): string {
  return sequence ? sequence.replace(/_/g, ' ') : '—';
}

/** A sequence and its zero-based step as staff read it: `fund · step 2`. */
export function sequenceStepLabel(sequence?: string, step?: number): string {
  return `${sequenceLabel(sequence)} · step ${(step ?? 0) + 1}`;
}

export const CHANNEL_POLICY_LABELS: Record<string, string> = {
  push_then_email: 'Push, or email if unopened',
  email_only: 'Email',
};

/** One sequence step's planned nudges: who gets it, and when the first goes. */
export interface PlannedStepGroup {
  key: string;
  sequence: string;
  step: number;
  channelPolicy: string;
  title: string;
  count: number;
  firstSendTime: string;
}

/**
 * Groups planned nudges by sequence and step, busiest step first. Ties keep
 * the step that goes out soonest first.
 */
export function groupPlannedByStep(planned: readonly PlannedNudge[]): PlannedStepGroup[] {
  const groups = new Map<string, PlannedStepGroup>();
  for (const p of planned) {
    const sequence = p.sequence ?? '';
    const step = p.step ?? 0;
    const key = `${sequence}:${step}`;
    const sendTime = p.sendTime ?? '';
    const group = groups.get(key);
    if (group) {
      group.count += 1;
      if (sendTime < group.firstSendTime) group.firstSendTime = sendTime;
      continue;
    }
    groups.set(key, {
      key,
      sequence,
      step,
      channelPolicy: p.channelPolicy ?? '',
      title: p.title ?? '',
      count: 1,
      firstSendTime: sendTime,
    });
  }
  return [...groups.values()].sort(
    (a, b) => b.count - a.count || a.firstSendTime.localeCompare(b.firstSendTime),
  );
}
